import { computed } from 'vue';
import { useTheme } from 'vuetify';

export function useThemeColors() {
  const theme = useTheme();

  // Primary theme colors from Vuetify theme
  const primary = computed(() => theme.current.value.colors.primary);
  const secondary = computed(() => theme.current.value.colors.secondary);
  const accent = computed(() => theme.current.value.colors.accent);
  const error = computed(() => theme.current.value.colors.error);
  const warning = computed(() => theme.current.value.colors.warning);
  const info = computed(() => theme.current.value.colors.info);
  const success = computed(() => theme.current.value.colors.success);

  // Common secondary colors (you can add more as needed)
  const blueGreyLighten4 = computed(() => '#CFD8DC');
  const blueGreyLighten5 = computed(() => '#ECEFF1');
  const blueGreyLighten1 = computed(() => '#78909C');
  const blueGreyLighten2 = computed(() => '#90A4AE');
  const blueGreyLighten3 = computed(() => '#B0BEC5');
  const blueGreyDarken1 = computed(() => '#546E7A');
  const blueGreyDarken2 = computed(() => '#455A64');
  const greyLighten2 = computed(() => '#E0E0E0');
  const greyLighten3 = computed(() => '#EEEEEE');
  const greyDarken1 = computed(() => '#757575');
  const lightBlueLighten2 = computed(() => '#81D4FA');
  const lightBlueLighten4 = computed(() => '#B3E5FC');
  const lightBlueLighten5 = computed(() => '#d0defb');
  const lightBlueDarken3 = computed(() => '#2563eb');
  const lightBlueDarken4 = computed(() => '#01579B');
  const orangeLighten5 = computed(() => '#FFF3E0');
  const amberAccent1 = computed(() => '#FFE57F');
  const amberAccent2 = computed(() => '#FFD740');
  const yellowAccent2 = computed(() => '#FFFF00');
  const yellowAccent3 = computed(() => '#FFEA00');
  const yellowLighten1 = computed(() => '#FBC02D');
  const yellowLighten3 = computed(() => '#FFF9C4');
  const yellowDarken4 = computed(() => '#F57F17');
  const deepOrangeDarken4 = computed(() => '#BF360C');
  const lightGreenLighten4 = computed(() => '#C8E6C9');
  const lightGreenLighten5 = computed(() => '#E8F5E9');
  const redLighten4 = computed(() => '#FFCDD2');
  const redLighten5 = computed(() => '#FFEBEE');
  const indigoLighten5 = computed(() => '#d0defb');
  const blueLighten5 = computed(() => '#d0defb');
  const greyLighten4 = computed(() => '#F5F5F5');
  const greyLighten5 = computed(() => '#FAFAFA');
  
  // Tailwind-style primary color variants for active states
  const primaryBg = computed(() => '#eff6ff');       // bg-primary-50 - light blue background
  const primaryBorder = computed(() => '#dbeafe');   // border-primary-100 - light blue border
  const primaryText = computed(() => '#1d4ed8');     // text-primary-700 - darker blue text
  
  // Primary color variants (lightest shade for selection backgrounds)
  const primaryLighten5 = computed(() => {
    // Create a very light tint of the primary color (95% lighter)
    const primaryColor = theme.current.value.colors.primary;
    // If primary is #1976D2 (default blue), this creates #E3F2FD (light blue)
    // This works for any primary color by extracting RGB and lightening
    const hex = primaryColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Mix with white (95% white, 5% primary color)
    const newR = Math.round(r * 0.05 + 255 * 0.95);
    const newG = Math.round(g * 0.05 + 255 * 0.95);
    const newB = Math.round(b * 0.05 + 255 * 0.95);
    
    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
  });

  // Backward compatibility aliases
  const purpleLighten5 = blueGreyLighten4; // Replace purple with blueGrey as requested

  return {
    // Theme colors
    primary,
    secondary,
    accent,
    error,
    warning,
    info,
    success,
    
    // Secondary colors
    blueGreyLighten1,
    blueGreyLighten2,
    blueGreyLighten3,
    blueGreyDarken1,
    blueGreyDarken2,
    blueGreyLighten4,
    blueGreyLighten5,
    greyLighten2,
    greyLighten3,
    greyDarken1,
    lightBlueLighten2,
    lightBlueLighten4,
    lightBlueLighten5,
    lightBlueDarken3,
    lightBlueDarken4,
    orangeLighten5,
    amberAccent1,
    amberAccent2,
    yellowAccent2,
    yellowAccent3,
    yellowLighten1,
    yellowLighten3,
    yellowDarken4,
    deepOrangeDarken4,
    lightGreenLighten4,
    lightGreenLighten5,
    redLighten4,
    redLighten5,
    indigoLighten5,
    blueLighten5,
    greyLighten4,
    greyLighten5,
    primaryLighten5,
    primaryBg,
    primaryBorder,
    primaryText,
    
    // Backward compatibility
    purpleLighten5,
  };
}